import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Command } from 'cmdk';
import {
  Sparkles, Globe, Terminal, Mail, Search as SearchIcon, Sheet, Database,
  MessageCircle, FileText, Calendar, Cloud, Upload, Download, FolderOpen,
  Link2, ShoppingBag, ScrollText, GitBranch, Webhook, Clock, Play,
  Zap, Minimize2, Maximize2, PanelLeft, X, CornerDownLeft, ArrowUp, ArrowDown,
  UserCheck, Shuffle, Tag,
} from 'lucide-react';
import { buildChipElement } from './TokenField';

/* ─── Static field maps ─────────────────────────────────────────── */

export const STEP_OUTPUTS = {
  ai_agent: [
    { path: 'response', label: 'Response', desc: 'Output from the last task' },
    { path: 'mode', label: 'Mode', desc: 'sequential / parallel' },
    { path: 'chat_history_id', label: 'Chat History ID', desc: '' },
  ],
  api_call: [
    { path: 'status_code', label: 'Status Code', desc: 'HTTP response code' },
    { path: 'response_body', label: 'Response Body', desc: 'Full response body' },
    { path: 'headers', label: 'Headers', desc: 'Response headers' },
    { path: 'url', label: 'URL', desc: 'Request URL' },
  ],
  code_execution: [
    { path: 'result', label: 'Result', desc: 'Value returned by the script' },
    { path: 'output', label: 'Output', desc: 'Script stdout' },
  ],
  gmail_send: [
    { path: 'message_id', label: 'Message ID', desc: '' },
    { path: 'thread_id', label: 'Thread ID', desc: '' },
  ],
  gmail_search: [
    { path: 'messages', label: 'Messages', desc: 'List of found messages' },
    { path: 'count', label: 'Count', desc: 'Number of results' },
    { path: 'messages[0].subject', label: 'First Subject', desc: '' },
    { path: 'messages[0].body', label: 'First Body', desc: '' },
  ],
  sheets_read_range: [
    { path: 'values', label: 'Values', desc: 'Cell data (2D array)' },
    { path: 'row_count', label: 'Row Count', desc: '' },
  ],
  sheets_append_row: [
    { path: 'updated_range', label: 'Updated Range', desc: '' },
    { path: 'updated_rows', label: 'Updated Rows', desc: '' },
  ],
  sheets_write_range: [
    { path: 'updated_range', label: 'Updated Range', desc: '' },
  ],
  sheets_create_spreadsheet: [
    { path: 'spreadsheet_id', label: 'Spreadsheet ID', desc: '' },
    { path: 'spreadsheet_url', label: 'URL', desc: '' },
  ],
  database: [
    { path: 'rows', label: 'Rows', desc: 'Result rows' },
    { path: 'count', label: 'Count', desc: 'Rows affected' },
  ],
  database_query: [
    { path: 'rows', label: 'Rows', desc: 'Query result rows' },
    { path: 'count', label: 'Count', desc: '' },
  ],
  whatsapp_notification: [
    { path: 'message_id', label: 'Message ID', desc: '' },
    { path: 'status', label: 'Status', desc: '' },
  ],
  docs_create_document: [
    { path: 'document_id', label: 'Document ID', desc: '' },
    { path: 'document_url', label: 'Document URL', desc: '' },
  ],
  docs_get_document: [
    { path: 'content', label: 'Content', desc: 'Document text content' },
    { path: 'document_id', label: 'Document ID', desc: '' },
  ],
  docs_batch_update: [
    { path: 'document_id', label: 'Document ID', desc: '' },
  ],
  calendar_create_event: [
    { path: 'event_id', label: 'Event ID', desc: '' },
    { path: 'event_url', label: 'Event URL', desc: '' },
  ],
  calendar_list_events: [
    { path: 'events', label: 'Events', desc: 'List of events' },
    { path: 'count', label: 'Count', desc: '' },
  ],
  drive_upload: [
    { path: 'file_id', label: 'File ID', desc: '' },
    { path: 'file_url', label: 'File URL', desc: '' },
  ],
  drive_download_file: [
    { path: 'content', label: 'Content', desc: 'File content' },
    { path: 'file_name', label: 'File Name', desc: '' },
  ],
  drive_list_files: [
    { path: 'files', label: 'Files', desc: 'List of files' },
    { path: 'count', label: 'Count', desc: '' },
  ],
  drive_share_file: [
    { path: 'success', label: 'Success', desc: '' },
  ],
  shopify_update_order: [
    { path: 'order_id', label: 'Order ID', desc: '' },
    { path: 'status', label: 'Status', desc: '' },
  ],
  wait_for_human_action: [
    { path: 'action_id', label: 'Action ID', desc: 'ID of the action selected by the human' },
    { path: 'action_label', label: 'Action Label', desc: 'Display label of the selected action' },
    { path: 'reason', label: 'Reason', desc: 'Optional reason provided by the human' },
    { path: 'responded_at', label: 'Responded At', desc: 'ISO timestamp when the human responded' },
    { path: 'respondent', label: 'Respondent', desc: 'Email of the human who responded' },
  ],
  execute_workflow: [
    { path: 'execution_id', label: 'Execution ID', desc: 'ID of the child workflow run' },
    { path: 'status', label: 'Status', desc: '"completed", "failed", etc.' },
    { path: 'output', label: 'Output', desc: 'Declared outputs from the child workflow' },
  ],
  log: [
    { path: 'logged', label: 'Logged', desc: '' },
  ],
};

export const TRIGGER_FIELDS = {
  webhook: [
    { path: 'body', label: 'Body', desc: 'Request body' },
    { path: 'headers', label: 'Headers', desc: 'HTTP headers' },
    { path: 'query', label: 'Query Params', desc: 'URL parameters' },
  ],
  whatsapp_inbound: [
    { path: 'from', label: 'From', desc: "Sender's number" },
    { path: 'message', label: 'Message', desc: 'Message content' },
    { path: 'media_url', label: 'Media URL', desc: 'Media file URL' },
    { path: 'message_type', label: 'Message Type', desc: 'text / image / document' },
  ],
  shopify_event: [
    { path: 'order.id', label: 'Order ID', desc: '' },
    { path: 'order.email', label: 'Customer Email', desc: '' },
    { path: 'order.total_price', label: 'Total Price', desc: '' },
    { path: 'event_type', label: 'Event Type', desc: '' },
  ],
  // Los tags son de la corrida, no del step: los dos devuelven el conjunto
  // completo tras el cambio, más lo que este step concreto pidió. Encadenar
  // sobre `tags` es lo que permite, por ejemplo, condicionar según si una
  // etiqueta acabó puesta.
  add_tag: [
    { path: 'tags', label: 'Tags', desc: 'Todas las etiquetas de la corrida tras el cambio' },
    { path: 'added', label: 'Added', desc: 'Las que pidió este step' },
  ],
  remove_tag: [
    { path: 'tags', label: 'Tags', desc: 'Todas las etiquetas de la corrida tras el cambio' },
    { path: 'removed', label: 'Removed', desc: 'Las que pidió este step' },
  ],
};

const STEP_ICONS = {
  ai_agent: Sparkles, api_call: Globe, code_execution: Terminal,
  gmail_send: Mail, gmail_search: SearchIcon,
  sheets_read_range: Sheet, sheets_append_row: Sheet, sheets_write_range: Sheet,
  sheets_create_spreadsheet: Sheet, database: Database, database_query: Database,
  whatsapp_notification: MessageCircle, docs_create_document: FileText, docs_get_document: FileText,
  docs_batch_update: FileText, calendar_create_event: Calendar, calendar_list_events: Calendar,
  drive_upload: Upload, drive_download_file: Download, drive_list_files: FolderOpen,
  drive_share_file: Link2, shopify_update_order: ShoppingBag, log: ScrollText, condition: GitBranch,
  wait_for_human_action: UserCheck,
  execute_workflow: Shuffle,
  // Sin entrada caen al fallback, que es `Sparkles` — el mismo icono que
  // `ai_agent`. No es un icono neutro: hace que un `add_tag` parezca un paso
  // de IA en el selector.
  //
  // El mismo glifo para los dos: lucide no tiene un icono de "quitar
  // etiqueta" —sólo `Tag` y `Tags`, y `TagIcon` es un alias de `Tag`—, y
  // usar la pila de etiquetas para `remove_tag` diría "varias", no "quitar".
  // Lo que distingue uno de otro es el nombre, que va al lado.
  add_tag: Tag,
  remove_tag: Tag,
};

const TRIGGER_ICONS = {
  webhook: Webhook, whatsapp_inbound: MessageCircle, shopify_event: ShoppingBag,
  scheduled: Clock, manual: Play,
};

/* ─── Helpers ───────────────────────────────────────────────────── */

export function resolveTokenLabel(token, nodes = []) {
  if (!token) return null;
  const m = token.match(/^\{\{(context|trigger):([^{}]+)\}\}$/);
  if (!m) return null;
  const kind = m[1];
  const path = m[2];

  if (kind === 'trigger') {
    const triggerNode = nodes.find((n) => n.type === 'trigger');
    const triggerType = triggerNode?.data?.trigger_type || 'webhook';
    const fields = TRIGGER_FIELDS[triggerType] || [];
    const f = fields.find((fd) => fd.path === path);
    return f ? f.label : path;
  }

  const stepMatch = path.match(/^step_(\d+)\.(.+)$/);
  if (!stepMatch) return path;
  const stepIdx = Number(stepMatch[1]);
  const fieldPath = stepMatch[2];
  const stepNodes = (nodes || [])
    .filter((n) => n.type !== 'trigger' && n.type !== 'satellite')
    .sort((a, b) => (a.position?.x ?? 0) - (b.position?.x ?? 0));
  const node = stepNodes[stepIdx];
  if (!node) return fieldPath;
  const stepType = node.data?.step_type || node.type;

  const taskMatch = fieldPath.match(/^outputs\[(\d+)\]\.output$/);
  if (taskMatch && stepType === 'ai_agent' && Array.isArray(node.data?.tasks)) {
    const task = node.data.tasks[Number(taskMatch[1])];
    if (task?.name) return task.name;
  }

  const fields = STEP_OUTPUTS[stepType] || [];
  const f = fields.find((fd) => fd.path === fieldPath);
  return f ? f.label : fieldPath;
}

function insertAtCursor(el, template, label) {
  if (!el) return false;

  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const newValue = el.value.slice(0, start) + template + el.value.slice(end);

    const proto =
      el.tagName === 'INPUT' ? HTMLInputElement.prototype : HTMLTextAreaElement.prototype;
    const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value').set;
    nativeSetter.call(el, newValue);
    el.dispatchEvent(new Event('input', { bubbles: true }));

    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + template.length, start + template.length);
    });
    return true;
  }

  if (el.dataset && el.dataset.tokenfield === 'true') {
    el.focus();
    const sel = window.getSelection();
    let range;
    if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
      range = sel.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    range.deleteContents();

    const chip = buildChipElement(template, label);
    range.insertNode(chip);

    const after = document.createRange();
    after.setStartAfter(chip);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);

    el.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  return false;
}

function getValueByPath(obj, path) {
  if (!obj || !path) return undefined;
  try {
    return path
      .replace(/\[(\d+)\]/g, '.$1')
      .split('.')
      .reduce((curr, key) => {
        if (curr === null || curr === undefined) return undefined;
        return curr[key];
      }, obj);
  } catch {
    return undefined;
  }
}

function formatValue(val, short = false) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed) return null;
    const limit = short ? 38 : 80;
    return trimmed.length > limit ? trimmed.slice(0, limit) + '…' : trimmed;
  }
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return `[${val.length} items]`;
  if (typeof val === 'object') return '{object}';
  return String(val);
}

/* ─── Group heading (Step / Trigger) ───────────────────────────── */

function GroupHeading({ number, Icon, label, kind }) {
  const isTrigger = kind === 'trigger';
  const accent = isTrigger
    ? { bg: 'from-orange-500/15 to-amber-500/[0.04]', ring: 'ring-orange-500/25', text: 'text-orange-300', dot: 'bg-orange-400' }
    : { bg: 'from-violet-500/15 to-fuchsia-500/[0.04]', ring: 'ring-violet-500/25', text: 'text-violet-300', dot: 'bg-violet-400' };

  return (
    <div className="flex items-center gap-2.5 px-3 pt-3 pb-1.5">
      <span className={`relative w-6 h-6 rounded-md bg-gradient-to-br ${accent.bg} ring-1 ${accent.ring} ${accent.text} flex items-center justify-center shrink-0`}>
        {Icon && <Icon className="w-3 h-3" strokeWidth={2.2} />}
        <span className={`absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-zinc-950 ring-1 ${accent.ring} text-[8.5px] font-semibold ${accent.text} flex items-center justify-center leading-none`}>
          {number}
        </span>
      </span>
      <span className="text-[11px] font-semibold text-white/85 tracking-tight truncate">
        {label}
      </span>
      <span className={`shrink-0 w-1 h-1 rounded-full ${accent.dot} opacity-60`} />
      <span className="text-[10px] uppercase tracking-[0.14em] text-white/30 font-medium truncate">
        {isTrigger ? 'Trigger' : 'Step output'}
      </span>
    </div>
  );
}

/* ─── Field row (inside Command.Item) ──────────────────────────── */

function FieldItem({ label, valuePreview, kind }) {
  const isTrigger = kind === 'trigger';
  const accentText = isTrigger ? 'text-orange-300' : 'text-violet-300';
  const accentBg = isTrigger ? 'bg-orange-500/10 border-orange-500/30' : 'bg-violet-500/10 border-violet-500/30';

  return (
    <>
      <span className="flex-1 min-w-0">
        <span className="block text-[12.5px] font-medium text-white/90 truncate leading-tight">
          {label}
        </span>
        <span className="block text-[11px] text-white/40 leading-snug truncate font-mono mt-0.5">
          {valuePreview || <span className="text-white/25 italic font-sans">no preview</span>}
        </span>
      </span>
      <span className={`token-insert-hint shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${accentText} ${accentBg} border`}>
        <CornerDownLeft className="w-2.5 h-2.5" strokeWidth={2.5} />
        Insert
      </span>
    </>
  );
}

/* ─── Footer with shortcuts ────────────────────────────────────── */

function Kbd({ icon: Icon, char }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-[5px] bg-white/[0.06] border border-white/[0.08] text-white/75 font-medium text-[10px] leading-none shadow-[inset_0_-1px_0_rgba(0,0,0,0.3)]">
      {Icon ? <Icon className="w-2.5 h-2.5" strokeWidth={2.5} /> : char}
    </kbd>
  );
}

function ShortcutsFooter() {
  return (
    <div className="px-3 py-2 border-t border-white/[0.04] flex items-center gap-3.5 shrink-0 bg-white/[0.012]">
      <span className="inline-flex items-center gap-1 text-[10px] text-white/45">
        <Kbd icon={ArrowUp} />
        <Kbd icon={ArrowDown} />
        Navigate
      </span>
      <span className="inline-flex items-center gap-1.5 text-[10px] text-white/45">
        <Kbd icon={CornerDownLeft} />
        Insert
      </span>
      <div className="flex-1" />
      <span className="inline-flex items-center gap-1.5 text-[10px] text-white/45">
        <Kbd char="Esc" />
        Close
      </span>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────── */

const DEFAULT_WIDTH = 440;
const DEFAULT_HEIGHT = 580;

function readPaletteRect() {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('[data-step-palette]');
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, width: r.width, height: r.height };
}

export default function DataSelector({ nodes, selectedNodeId, visible, stepResults = {}, lastInputRef, onClose }) {
  const [search, setSearch] = React.useState('');
  const [paletteRect, setPaletteRect] = React.useState(() => readPaletteRect());
  const [expanded, setExpanded] = React.useState(false);
  const [position, setPosition] = React.useState(() => {
    const r = readPaletteRect();
    if (r) return { x: r.x, y: r.y };
    return {
      x: typeof window !== 'undefined' ? Math.max(24, window.innerWidth * 0.06) : 60,
      y: typeof window !== 'undefined' ? Math.max(80, window.innerHeight * 0.15) : 100,
    };
  });
  const [isDragging, setIsDragging] = React.useState(false);
  const dragOffsetRef = React.useRef({ x: 0, y: 0 });

  React.useEffect(() => {
    if (!visible) return;
    const update = () => {
      const r = readPaletteRect();
      if (r) setPaletteRect(r);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [visible]);

  React.useEffect(() => {
    if (!visible) return;
    const r = readPaletteRect();
    if (r) {
      setPaletteRect(r);
      setPosition({ x: r.x, y: r.y });
    }
  }, [visible]);

  React.useEffect(() => {
    if (expanded && paletteRect) {
      setPosition({ x: paletteRect.x, y: paletteRect.y });
    }
  }, [expanded, paletteRect]);

  const selectedNode = (nodes || []).find((n) => n.id === selectedNodeId);
  const selectedX = selectedNode?.position?.x ?? Infinity;

  const triggerNodes = (nodes || []).filter((n) => n.type === 'trigger');
  const allStepNodes = (nodes || [])
    .filter((n) => n.type !== 'trigger' && n.type !== 'satellite')
    .sort((a, b) => (a.position?.x ?? 0) - (b.position?.x ?? 0));

  const previousSteps = allStepNodes
    .filter((n) => n.id !== selectedNodeId && (n.position?.x ?? 0) < selectedX)
    .map((n) => ({ ...n, positionIdx: allStepNodes.indexOf(n) }))
    .sort((a, b) => (b.position?.x ?? 0) - (a.position?.x ?? 0))
    .slice(0, 1);

  const groups = React.useMemo(() => {
    const out = [];
    if (previousSteps.length > 0) {
      previousSteps.forEach((sn) => {
        const stepType = sn.data?.step_type || sn.type;
        const staticFields = STEP_OUTPUTS[stepType] || [];
        const taskFields =
          stepType === 'ai_agent' && Array.isArray(sn.data?.tasks)
            ? sn.data.tasks
                .map((task, idx) => ({
                  path: `outputs[${idx}].output`,
                  label: task.name || `Task ${idx + 1}`,
                  desc: 'Task output',
                }))
                .filter((tf) => !staticFields.some((sf) => sf.path === tf.path))
            : [];
        const fields = [...staticFields, ...taskFields];
        const stepRef = `step_${sn.positionIdx}`;
        const stepData = stepResults[stepRef] || stepResults[sn.data?.step_id] || {};
        out.push({
          id: sn.id,
          kind: 'step',
          number: sn.positionIdx + 1,
          Icon: STEP_ICONS[stepType] || Sparkles,
          label: sn.data?.label || stepType,
          fields: fields.map((f) => ({
            ...f,
            template: `{{context:${stepRef}.${f.path}}}`,
            valuePreview: formatValue(getValueByPath(stepData, f.path), false),
          })),
        });
      });
    } else {
      triggerNodes.forEach((tn, idx) => {
        const triggerType = tn.data?.trigger_type || 'webhook';
        const fields = TRIGGER_FIELDS[triggerType] || [];
        out.push({
          id: tn.id,
          kind: 'trigger',
          number: idx + 1,
          Icon: TRIGGER_ICONS[triggerType] || Webhook,
          label: tn.data?.label || 'Trigger',
          fields: fields.map((f) => ({
            ...f,
            template: `{{trigger:${f.path}}}`,
            valuePreview: null,
          })),
        });
      });
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, selectedNodeId, stepResults]);

  const hasContent = groups.length > 0;

  const handleInsert = (template, label) => {
    insertAtCursor(lastInputRef?.current, template, label);
  };

  /* ── Drag handling ──────────────────────────────────────────── */
  const handleDragStart = (e) => {
    if (expanded) return;
    setIsDragging(true);
    dragOffsetRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    e.preventDefault();
  };

  React.useEffect(() => {
    if (!isDragging) return;
    const handleMove = (e) => {
      const newX = e.clientX - dragOffsetRef.current.x;
      const newY = e.clientY - dragOffsetRef.current.y;
      const maxX = window.innerWidth - DEFAULT_WIDTH;
      const maxY = window.innerHeight - 100;
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };
    const handleUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDragging]);

  // Esc closes
  React.useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose && onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, onClose]);

  if (typeof document === 'undefined') return null;

  const panelWidth = paletteRect ? paletteRect.width : DEFAULT_WIDTH;
  const panelHeight = expanded && paletteRect ? paletteRect.height : DEFAULT_HEIGHT;

  return createPortal(
    <AnimatePresence>
      {visible && hasContent && (
        <motion.div
          data-dataselector="true"
          initial={{ opacity: 0, scale: 0.97, y: 6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 6 }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="fixed flex flex-col rounded-2xl overflow-hidden data-selector-panel"
          style={{
            top: position.y,
            left: position.x,
            width: panelWidth,
            height: panelHeight,
            maxHeight: 'calc(100vh - 80px)',
            background:
              'linear-gradient(180deg, rgba(20, 20, 26, 0.92) 0%, rgba(11, 11, 16, 0.94) 100%)',
            backdropFilter: 'blur(28px) saturate(160%)',
            WebkitBackdropFilter: 'blur(28px) saturate(160%)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            boxShadow:
              '0 30px 80px -20px rgba(0, 0, 0, 0.85), 0 0 0 1px rgba(139, 92, 246, 0.06), 0 0 80px -10px rgba(139, 92, 246, 0.16)',
            zIndex: 10000,
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* Top accent line + ambient glow */}
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-violet-400/50 to-transparent pointer-events-none" />
          <div
            className="absolute -top-24 left-1/2 -translate-x-1/2 w-[60%] h-32 pointer-events-none opacity-60"
            style={{
              background:
                'radial-gradient(closest-side, rgba(139, 92, 246, 0.18), transparent 70%)',
            }}
          />

          {/* Header — draggable */}
          <div
            className={`relative px-3.5 py-3 border-b border-white/[0.05] shrink-0 select-none ${
              expanded ? '' : isDragging ? 'cursor-grabbing' : 'cursor-grab'
            }`}
            onMouseDown={handleDragStart}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="relative w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500/30 to-fuchsia-500/15 flex items-center justify-center ring-1 ring-violet-500/30 shadow-[0_4px_12px_-2px_rgba(139,92,246,0.4)]">
                  <Zap className="w-3.5 h-3.5 text-violet-200" strokeWidth={2.5} fill="currentColor" />
                </div>
                <div className="leading-tight">
                  <p className="text-[13px] font-semibold text-white tracking-tight">Data Selector</p>
                  <p className="text-[10px] text-white/45 font-medium">Reference outputs from previous steps</p>
                </div>
              </div>

              <div className="flex items-center gap-0.5">
                <button
                  className="w-7 h-7 rounded-md hover:bg-white/[0.06] flex items-center justify-center transition-all text-white/50 hover:text-white active:scale-95"
                  title={expanded ? 'Restore' : 'Expand to sidebar'}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
                >
                  {expanded ? (
                    <Minimize2 className="w-3.5 h-3.5" strokeWidth={2} />
                  ) : (
                    <Maximize2 className="w-3.5 h-3.5" strokeWidth={2} />
                  )}
                </button>
                <button
                  className="w-7 h-7 rounded-md hover:bg-white/[0.06] flex items-center justify-center transition-all text-white/50 hover:text-white active:scale-95"
                  title="Reset position"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const r = readPaletteRect();
                    if (r) setPosition({ x: r.x, y: r.y });
                  }}
                >
                  <PanelLeft className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
                <button
                  className="w-7 h-7 rounded-md hover:bg-red-500/15 flex items-center justify-center transition-all text-white/50 hover:text-red-300 active:scale-95"
                  title="Close (Esc)"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={(e) => { e.stopPropagation(); onClose && onClose(); }}
                >
                  <X className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </div>
            </div>
          </div>

          {/* Command palette */}
          <Command
            className="flex-1 flex flex-col min-h-0 data-selector-cmd"
            loop
            label="Data selector"
          >
            {/* Search */}
            <div className="px-3 pt-3 pb-2 shrink-0">
              <div className="relative group">
                <SearchIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/35 pointer-events-none transition-colors group-focus-within:text-violet-300"
                  strokeWidth={2.5}
                />
                <Command.Input
                  value={search}
                  onValueChange={setSearch}
                  placeholder="Search fields, steps…"
                  className="w-full pl-9 pr-16 py-2.5 text-[12.5px] bg-white/[0.025] border border-white/[0.06] rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-violet-500/50 focus:bg-white/[0.04] focus:ring-2 focus:ring-violet-500/15 transition-all"
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <kbd className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-[18px] px-1.5 rounded bg-white/[0.04] border border-white/[0.06] text-[9.5px] text-white/40 font-medium pointer-events-none">
                  /
                </kbd>
              </div>
            </div>

            {/* List */}
            <Command.List className="flex-1 overflow-y-auto px-1.5 pb-2 data-selector-list">
              <Command.Empty>
                <div className="flex flex-col items-center justify-center py-14 px-4 text-center">
                  <div className="w-11 h-11 rounded-full bg-white/[0.03] ring-1 ring-white/5 flex items-center justify-center mb-3">
                    <SearchIcon className="w-4 h-4 text-white/35" strokeWidth={2} />
                  </div>
                  <p className="text-[12px] text-white/55 font-medium">
                    No matching fields
                  </p>
                  <p className="text-[11px] text-white/30 mt-1">
                    Try a different search term
                  </p>
                </div>
              </Command.Empty>

              {groups.map((group) => (
                <Command.Group
                  key={group.id}
                  heading={
                    <GroupHeading
                      number={group.number}
                      Icon={group.Icon}
                      label={group.label}
                      kind={group.kind}
                    />
                  }
                >
                  {group.fields.length === 0 ? (
                    <div className="px-4 py-2 text-[11px] text-white/30 italic">
                      No outputs available
                    </div>
                  ) : (
                    group.fields.map((f) => (
                      <Command.Item
                        key={`${group.id}-${f.path}`}
                        value={`${group.label} ${f.label} ${f.path}`}
                        keywords={[f.label, f.path, group.label]}
                        onSelect={() => handleInsert(f.template, f.label)}
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        data-kind={group.kind}
                        className="data-selector-item flex items-center gap-3 px-3 py-2 mx-1 rounded-lg cursor-pointer relative"
                      >
                        <span className="data-selector-item-bar shrink-0 w-[3px] self-stretch rounded-full" />
                        <FieldItem
                          label={f.label}
                          valuePreview={f.valuePreview}
                          kind={group.kind}
                        />
                      </Command.Item>
                    ))
                  )}
                </Command.Group>
              ))}
            </Command.List>

            <ShortcutsFooter />
          </Command>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
